import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, MapPin, Heart, Gift, Settings, LogOut, Plus, Trash2, Pencil, Loader2, Shield, AlertTriangle, ClipboardList, ChevronRight, MessageCircle, Copy, X } from "lucide-react";
import api, { money, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { QRCodeCanvas } from "qrcode.react";
import { Barcode } from "@/components/Barcode";
import ProductModal from "@/components/ProductModal";
import ProductCard from "@/components/ProductCard";
import OrderChatModal from "@/components/OrderChatModal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Account() {
  const { user, loading, logout, setUser } = useAuth();
  const { addItem, items: cartItems, setOpen: setCartOpen } = useCart();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (user && user.welcome_bonus_points > 0 && !user.welcome_seen) {
      setShowWelcome(true);
      api.post("/auth/welcome-seen").then(() => setUser((u) => (u ? { ...u, welcome_seen: true } : u))).catch((e) => console.warn("welcome-seen failed", e));
    }
  }, [user?.id]); // eslint-disable-line
  const [addresses, setAddresses] = useState([]);
  const [favs, setFavs] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [referral, setReferral] = useState(null);
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(null);
  const [chatOrder, setChatOrder] = useState(null);
  const [newAddr, setNewAddr] = useState({ label: "Home", line1: "", line2: "", postcode: "", instructions: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [editingId, setEditingId] = useState(null);
  const [editAddr, setEditAddr] = useState({ label: "", line1: "", line2: "", postcode: "", instructions: "" });

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => {
    if (user) {
      setProfile({ name: user.name, phone: user.phone || "" });
      api.get("/account/addresses").then(({ data }) => setAddresses(data)).catch(() => {});
      api.get("/account/favourites").then(({ data }) => setFavs(data)).catch(() => {});
      api.get("/account/rewards").then(({ data }) => {
        setRewards(data);
        if (data && data.points != null && data.points !== user.loyalty_points) {
          setUser((u) => (u ? { ...u, loyalty_points: data.points } : u));
        }
      }).catch(() => {});
      api.get("/account/orders").then(({ data }) => setOrders(data)).catch(() => {});
      api.get("/account/referral").then(({ data }) => setReferral(data)).catch(() => {});
    }
  }, [user]);

  const committedRewardPoints = (cartItems || []).reduce((sum, i) => sum + (i.reward_item_id ? (i.reward_points || 0) : 0), 0);
  const availableRewardPoints = (rewards?.points || 0) - committedRewardPoints;
  const redeemReward = (r) => {
    if (availableRewardPoints < r.points) { toast.error("Not enough points to redeem this reward."); return; }
    addItem({
      product_id: r.product_id || null, name: r.title, image: "",
      price: r.product_id ? 0 : -(r.value || 0), quantity: 1,
      reward_item_id: r.id, reward_points: r.points, selected_options: [], special_instructions: "",
    });
    setCartOpen(true);
    toast.success(`${r.title} added — it'll be applied at checkout`);
  };

  if (loading || !user) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-metros-red" size={32} /></div>;

  const addAddress = async () => {
    if (!newAddr.line1 || !newAddr.postcode) return toast.error("Address and postcode required");
    const { data } = await api.post("/account/addresses", newAddr);
    setAddresses([...addresses, data]); setNewAddr({ label: "Home", line1: "", line2: "", postcode: "", instructions: "" });
    toast.success("Address saved");
  };
  const delAddress = async (id) => { await api.delete(`/account/addresses/${id}`); setAddresses(addresses.filter((a) => a.id !== id)); };
  const startEdit = (a) => { setEditingId(a.id); setEditAddr({ label: a.label || "Home", line1: a.line1 || "", line2: a.line2 || "", postcode: a.postcode || "", instructions: a.instructions || "" }); };
  const saveEdit = async (id) => {
    if (!editAddr.line1 || !editAddr.postcode) return toast.error("Address and postcode required");
    try { const { data } = await api.put(`/account/addresses/${id}`, editAddr); setAddresses(addresses.map((a) => (a.id === id ? { ...data } : a))); setEditingId(null); toast.success("Address updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveProfile = async () => { const { data } = await api.put("/account/profile", profile); setUser(data); toast.success("Profile updated"); };
  const changePw = async () => {
    try { await api.post("/auth/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleFav = async (p) => { await api.post(`/account/favourites/${p.id}`); setFavs(favs.filter((f) => f.id !== p.id)); toast.success("Removed from favourites"); };
  const deleteAccount = async () => {
    try { await api.delete("/account"); toast.success("Your account has been deleted"); logout(); navigate("/"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const TABS = [
    { k: "overview", label: "Overview", Icon: User },
    { k: "orders", label: "Orders", Icon: ClipboardList },
    { k: "addresses", label: "Addresses", Icon: MapPin },
    { k: "favourites", label: "Favourites", Icon: Heart },
    { k: "rewards", label: "Rewards", Icon: Gift },
    { k: "settings", label: "Settings", Icon: Settings },
  ];

  const orv = rewards?.order_reward;
  const rewardTracker = orv?.enabled ? (
    <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="reward-tracker">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Gift size={18} className="text-metros-red" /> {orv.percent}% off reward</h3>
        <span className="text-sm font-bold text-metros-red" data-testid="reward-tracker-status">{orv.is_next_reward ? "Ready on your next order!" : `${orv.until_reward} to go`}</span>
      </div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-gray-500" data-testid="reward-tracker-count">
          {orv.position} of {orv.every} orders
        </span>
        <span className="text-xs text-gray-400">Reward on order {orv.every}</span>
      </div>
      <div className="flex items-center gap-1.5">
        {Array.from({ length: orv.every }).map((_, i) => (
          <div key={i} className={`h-2.5 flex-1 rounded-full transition-all ${(orv.is_next_reward || i < orv.position) ? "bg-metros-red" : "bg-gray-100"}`} />
        ))}
      </div>
      <p className="text-sm text-muted-foreground mt-3">
        {orv.is_next_reward
          ? `Your next order gets ${orv.percent}% off the food subtotal — the discount applies automatically at checkout. 🎉`
          : `Order ${orv.until_reward} more time${orv.until_reward === 1 ? "" : "s"} to unlock ${orv.percent}% off. You've completed ${orv.completed} order${orv.completed === 1 ? "" : "s"} so far.`}
      </p>
    </div>
  ) : null;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-metros-red font-bold text-xs uppercase tracking-widest">Welcome back</p>
          <h1 className="font-heading font-extrabold text-3xl">{user.name}</h1>
          <p className="text-sm text-muted-foreground">Good to see you again.</p>
        </div>
        <div className="flex items-center gap-2">
          {user.role === "admin" && (
            <button onClick={() => navigate("/admin")} className="flex items-center gap-2 bg-metros-black text-white rounded-full px-4 py-2.5 text-sm font-semibold"><Shield size={16} /> Admin</button>
          )}
          <button data-testid="account-signout" onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 border border-gray-200 rounded-full px-4 py-2.5 text-sm font-semibold text-metros-red hover:bg-red-50 transition-colors"><LogOut size={16} /> Sign Out</button>
        </div>
      </div>

      {showWelcome && (
        <div data-testid="welcome-bonus-card" className="relative bg-metros-red text-white rounded-2xl p-5 mb-6 flex items-center gap-4 animate-in fade-in slide-in-from-top-2 duration-500">
          <div className="bg-white/15 rounded-full p-3 shrink-0"><Gift size={24} /></div>
          <div className="min-w-0">
            <p className="font-heading font-extrabold text-lg md:text-xl">You've got {user.welcome_bonus_points} welcome points!</p>
            <p className="text-sm text-white/85">They're already in your balance — earn more with every order and redeem them for rewards.</p>
          </div>
          <button data-testid="welcome-bonus-dismiss" aria-label="Dismiss" onClick={() => setShowWelcome(false)} className="absolute top-3 right-3 text-white/70 hover:text-white"><X size={18} /></button>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6">
        {TABS.map(({ k, label, Icon }) => (
          <button key={k} data-testid={`account-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap ${tab === k ? "bg-metros-red text-white" : "bg-gray-100 text-gray-700"}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid md:grid-cols-3 gap-4">
          {rewardTracker && <div className="md:col-span-3">{rewardTracker}</div>}
          <div className="bg-metros-black text-white grain rounded-2xl p-6">
            <Gift className="text-metros-red mb-2" /><p className="text-sm text-gray-400">Reward points</p>
            <p className="font-heading font-extrabold text-3xl">{rewards?.points ?? 0}</p>
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl p-6"><p className="text-sm text-muted-foreground">Total orders</p><p className="font-heading font-extrabold text-3xl">{orders.length}</p></div>
          <div className="bg-white border border-gray-100 rounded-2xl p-6"><p className="text-sm text-muted-foreground">Saved addresses</p><p className="font-heading font-extrabold text-3xl">{addresses.length}</p></div>
          <div className="md:col-span-3">
            <h3 className="font-heading font-bold text-lg mb-2">Order again</h3>
            {orders.length === 0 ? <p className="text-sm text-muted-foreground">No orders yet.</p> : (
              <div className="space-y-2">
                {orders.slice(0, 3).map((o) => (
                  <div key={o.id} className="flex justify-between items-center bg-white border border-gray-100 rounded-xl p-3">
                    <span className="text-sm">{o.order_number} · {money(o.total)}</span>
                    <button onClick={() => navigate(`/order/${o.order_number}`)} className="text-sm font-semibold text-metros-red">View</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3" data-testid="account-orders">
          {orders.length === 0 ? (
            <div className="text-center py-16"><ClipboardList className="mx-auto text-gray-300" size={40} /><p className="font-heading font-bold text-xl mt-3">No orders yet</p><button onClick={() => navigate("/menu")} className="mt-4 bg-metros-red text-white rounded-full px-6 py-3 font-semibold">Start Ordering</button></div>
          ) : orders.map((o) => (
            <div key={o.id} data-testid={`account-order-${o.order_number}`} className="bg-white border border-gray-100 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div><p className="font-heading font-bold">{o.order_number}</p><p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.type}</p></div>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">{o.status.replace(/_/g, " ")}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="font-heading font-bold">{money(o.total)}</span>
                <div className="flex gap-2">
                  <button data-testid={`order-chat-btn-${o.order_number}`} onClick={() => setChatOrder(o)} className="flex items-center gap-1 text-sm font-semibold border border-gray-200 rounded-full px-4 py-2 hover:border-metros-red"><MessageCircle size={14} /> Chat</button>
                  <button onClick={() => navigate(`/order/${o.order_number}`)} className="flex items-center gap-1 text-sm font-semibold bg-metros-black text-white rounded-full px-4 py-2">View / Track <ChevronRight size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "addresses" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            {addresses.map((a) => (
              <div key={a.id} className="bg-white border border-gray-100 rounded-2xl p-4">
                {editingId === a.id ? (
                  <div className="space-y-2" data-testid={`edit-addr-form-${a.id}`}>
                    <input data-testid={`edit-label-${a.id}`} placeholder="Label (Home/Work)" value={editAddr.label} onChange={(e) => setEditAddr({ ...editAddr, label: e.target.value })} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
                    <input data-testid={`edit-line1-${a.id}`} placeholder="Address line 1" value={editAddr.line1} onChange={(e) => setEditAddr({ ...editAddr, line1: e.target.value })} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
                    <input data-testid={`edit-line2-${a.id}`} placeholder="Address line 2 (optional)" value={editAddr.line2} onChange={(e) => setEditAddr({ ...editAddr, line2: e.target.value })} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
                    <input data-testid={`edit-postcode-${a.id}`} placeholder="Postcode" value={editAddr.postcode} onChange={(e) => setEditAddr({ ...editAddr, postcode: e.target.value })} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm uppercase" />
                    <div className="flex gap-2">
                      <button data-testid={`save-edit-${a.id}`} onClick={() => saveEdit(a.id)} className="flex-1 bg-metros-red text-white rounded-full py-2 text-sm font-semibold">Save</button>
                      <button data-testid={`cancel-edit-${a.id}`} onClick={() => setEditingId(null)} className="px-4 rounded-full border border-gray-200 text-sm font-semibold">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-start">
                    <div><p className="font-semibold">{a.label}</p><p className="text-sm text-muted-foreground">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.postcode}</p></div>
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(a)} data-testid={`edit-addr-${a.id}`} className="text-gray-400 hover:text-metros-black"><Pencil size={17} /></button>
                      <button onClick={() => delAddress(a.id)} data-testid={`del-addr-${a.id}`} className="text-gray-400 hover:text-metros-red"><Trash2 size={18} /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {addresses.length === 0 && <p className="text-sm text-muted-foreground">No saved addresses.</p>}
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-3">
            <h3 className="font-heading font-bold">Add address</h3>
            <input data-testid="addr-label" placeholder="Label (Home/Work)" value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <input data-testid="addr-line1" placeholder="Address line 1" value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <input data-testid="addr-postcode" placeholder="Postcode" value={newAddr.postcode} onChange={(e) => setNewAddr({ ...newAddr, postcode: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm uppercase" />
            <button data-testid="save-addr" onClick={addAddress} className="w-full bg-metros-red text-white rounded-full py-2.5 font-semibold flex items-center justify-center gap-1"><Plus size={16} /> Save Address</button>
          </div>
        </div>
      )}

      {tab === "favourites" && (
        favs.length === 0 ? (
          <div className="text-center py-16" data-testid="no-favs"><Heart className="mx-auto text-gray-300" size={40} /><p className="font-heading font-bold text-xl mt-3">No favourites yet</p><p className="text-muted-foreground text-sm">Tap the heart on a product to save it.</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {favs.map((p) => <ProductCard key={p.id} product={p} onOpen={setModal} />)}
          </div>
        )
      )}

      {tab === "rewards" && rewards && (
        <div className="space-y-4">
          {rewardTracker}
          <div className="bg-metros-black text-white grain rounded-2xl p-8">
            <p className="text-sm text-gray-400">Your points</p>
            <p className="font-heading font-extrabold text-5xl text-metros-red">{rewards.points}</p>
            <p className="text-sm text-gray-400 mt-2">{rewards.config?.reward_value ? Math.round(rewards.config.reward_threshold / rewards.config.reward_value) : 20} points = £1 off · redeem any amount at checkout</p>
            {user?.email && (
              <div className="mt-5 bg-white rounded-2xl p-4 inline-flex flex-col items-center" data-testid="loyalty-qr">
                <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wide mb-1.5">QR Code</p>
                <QRCodeCanvas value={`METROS-LOYALTY:${user.email}`} size={132} />
                {rewards?.loyalty_code && (
                  <>
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wide mt-4 mb-1.5">Barcode</p>
                    <Barcode value={rewards.loyalty_code} className="max-w-full h-auto" />
                  </>
                )}
                <p className="text-[11px] text-gray-500 mt-2 font-semibold">Show to staff for walk-in points</p>
                <p className="text-[10px] text-gray-400 mt-1">Or give staff your code:</p>
                <button
                  type="button"
                  data-testid="loyalty-code"
                  onClick={() => { navigator.clipboard?.writeText(`METROS-LOYALTY:${user.email}`); toast.success("Loyalty code copied!"); }}
                  className="mt-0.5 inline-flex items-center gap-1.5 font-mono text-xs font-bold text-metros-black tracking-wide break-all rounded-md px-2 py-1 hover:bg-gray-100 active:scale-95 transition-all"
                  title="Tap to copy"
                >
                  METROS-LOYALTY:{user.email}
                  <Copy size={12} className="text-gray-400 shrink-0" />
                </button>
              </div>
            )}
          </div>
          {referral && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6" data-testid="referral-card">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Gift size={18} className="text-metros-red" /> Invite friends, earn {referral.points_per_referral} points</h3>
              <p className="text-sm text-muted-foreground mt-1">Share your code. When a friend places their first order, you get <b>{referral.points_per_referral} points</b>.</p>
              <div className="flex flex-col sm:flex-row items-stretch gap-2 mt-4">
                <div className="flex-1 flex items-center justify-between bg-gray-50 border border-dashed border-gray-300 rounded-xl px-4 py-3">
                  <span className="font-heading font-extrabold tracking-widest text-metros-black" data-testid="referral-code">{referral.code}</span>
                  <button data-testid="referral-copy-code" onClick={() => { navigator.clipboard?.writeText(referral.code); toast.success("Code copied!"); }} className="text-sm font-semibold text-metros-red">Copy</button>
                </div>
                <button data-testid="referral-copy-link" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/?ref=${referral.code}`); toast.success("Invite link copied!"); }} className="bg-metros-red text-white rounded-xl px-5 py-3 text-sm font-bold whitespace-nowrap">Copy invite link</button>
              </div>
              <p className="text-sm text-muted-foreground mt-3" data-testid="referral-stats">
                <b className="text-metros-black">{referral.invited}</b> friend{referral.invited === 1 ? "" : "s"} joined · <b className="text-metros-red">{referral.points_earned} points</b> earned so far
              </p>
              {referral.welcome_points > 0 && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="referral-welcome-note">Your friend also gets <b>{referral.welcome_points} points</b> on their first order.</p>
              )}
              <div className="flex gap-2 mt-3">
                <a data-testid="referral-share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(`Order from METROS with my link and we both earn reward points 🎁 ${window.location.origin}/?ref=${referral.code}`)}`} target="_blank" rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white bg-[#25D366] hover:opacity-90 transition-opacity"><MessageCircle size={16} /> WhatsApp</a>
                <a data-testid="referral-share-sms" href={`sms:?&body=${encodeURIComponent(`Order from METROS with my link and we both earn reward points: ${window.location.origin}/?ref=${referral.code}`)}`}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white bg-metros-black hover:bg-black transition-colors"><MessageCircle size={16} /> Messages</a>
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-3">
            {rewards.reward_items?.map((r) => {
              const inCart = (cartItems || []).some((i) => i.reward_item_id === r.id);
              const canRedeem = !inCart && availableRewardPoints >= r.points;
              return (
              <div key={r.id} className="bg-white border border-gray-100 rounded-2xl p-5 text-center">
                <Gift className="mx-auto text-metros-red mb-2" />
                <p className="font-heading font-bold">{r.title}</p>
                <p className="text-sm text-muted-foreground">{r.points} points{r.product_id ? " · free item" : ""}</p>
                <button data-testid={`redeem-${r.id}`} disabled={!canRedeem} onClick={() => redeemReward(r)} className="mt-3 w-full bg-metros-red text-white rounded-full py-2 text-sm font-semibold disabled:opacity-40">{inCart ? "Added ✓" : (availableRewardPoints >= r.points ? "Redeem" : "Locked")}</button>
              </div>
              );
            })}
          </div>
          <div>
            <h3 className="font-heading font-bold mb-2">Points history</h3>
            {rewards.history?.length ? rewards.history.map((h) => (
              <div key={h.order_number} className="flex justify-between text-sm border-b border-gray-50 py-2"><span>{h.order_number}</span><span className="text-metros-red font-semibold">+{h.points_earned}</span></div>
            )) : <p className="text-sm text-muted-foreground">No points yet.</p>}
          </div>
        </div>
      )}

      {tab === "settings" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-3">
            <h3 className="font-heading font-bold">Personal details</h3>
            <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <input data-testid="profile-phone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Phone" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <button data-testid="save-profile" onClick={saveProfile} className="w-full bg-metros-black text-white rounded-full py-2.5 font-semibold">Save</button>
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-3">
            <h3 className="font-heading font-bold">Change password</h3>
            <input data-testid="pw-current" type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <input data-testid="pw-new" type="password" placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <button data-testid="change-pw" onClick={changePw} className="w-full bg-metros-red text-white rounded-full py-2.5 font-semibold">Update Password</button>
          </div>
          <button data-testid="logout" onClick={() => { logout(); navigate("/"); }} className="flex items-center justify-center gap-2 border border-gray-200 rounded-full py-3 font-semibold text-metros-red md:col-span-2"><LogOut size={16} /> Sign Out</button>
          <div className="md:col-span-2 border border-red-200 bg-red-50/50 rounded-2xl p-5">
            <h3 className="font-heading font-bold text-metros-red flex items-center gap-2"><AlertTriangle size={18} /> Delete account</h3>
            <p className="text-sm text-muted-foreground mt-1">Permanently remove your account and personal details. Past orders are kept anonymously for records. This cannot be undone.</p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="delete-account" className="mt-3 bg-metros-red text-white rounded-full px-5 py-2.5 text-sm font-bold">Delete My Account</button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="delete-account-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently deletes your METROS account, saved addresses and favourites. You can't undo this.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="delete-confirm" onClick={deleteAccount} className="bg-metros-red hover:bg-metros-redhover">Delete Account</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      <ProductModal product={modal} open={!!modal} onClose={() => setModal(null)} onFavourite={toggleFav} isFav={true} />
      {chatOrder && <OrderChatModal order={chatOrder} viewer="customer" onClose={() => setChatOrder(null)} />}
    </div>
  );
}
